import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminCompetenceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import {
  CreatePrivilegeTemplate,
  DeletePrivilegeTemplate,
  LoadAllPrivilegeTemplates,
  LoadPrivilegeTemplates,
  LoadPrivilegeTemplateById,
  UpdatePrivilegeTemplate,
  ClearCurrentPrivilegeTemplate,
} from './privilege-template.actions';
import { PrivilegeTemplateApiService } from '../services/privilege-template-api.service';
import { IPrivilegeTemplateModel } from '../model/privilege-template.model';

export interface PrivilegeTemplateStateModel {
  privilegeTemplates: IPrivilegeTemplateModel[];
  allPrivilegeTemplates: IdName[];
  privilegeTemplatesTotalPages: number;
  includeGloballyAvailableForProductOwners: boolean;
  includeTenantRelated: boolean;
  currentPrivilegeTemplate: IPrivilegeTemplateModel | undefined;
}

const PRIVILEGE_TEMPLATE_STATE_TOKEN = new StateToken<PrivilegeTemplateStateModel>(
  'privilegeTemplates'
);

@State<PrivilegeTemplateStateModel>({
  name: PRIVILEGE_TEMPLATE_STATE_TOKEN,
  defaults: {
    privilegeTemplates: [],
    allPrivilegeTemplates: [],
    privilegeTemplatesTotalPages: 0,
    includeTenantRelated: true,
    includeGloballyAvailableForProductOwners: false,
    currentPrivilegeTemplate: undefined,
  },
})
@Injectable()
export class PrivilegeTemplateState {
  constructor(private service: PrivilegeTemplateApiService) {}

  @Action(LoadPrivilegeTemplates)
  loadPrivilegeTemplates(
    { getState, setState }: StateContext<PrivilegeTemplateStateModel>,
    { filter }: LoadPrivilegeTemplates
  ) {
    return this.service.loadPrivilegeTemplates(filter).pipe(
      tap((response: ApiResponse<IPrivilegeTemplateModel[]>) => {
        const state = getState();
        let newState = {
          ...state,
          privilegeTemplates: response.data,
          privilegeTemplatesTotalPages: response.totalPages,
        };
        if (filter?.additional !== null && filter?.additional !== undefined) {
          newState = {
            ...newState,
            includeTenantRelated: filter.additional.includeTenantRelated,
            includeGloballyAvailableForProductOwners:
              filter.additional.includeGloballyAvailableForProductOwners,
          };
        }
        setState(newState);
      })
    );
  }

  @Action(LoadAllPrivilegeTemplates, { cancelUncompleted: true })
  loadAllPrivilegeTemplates(ctx: StateContext<PrivilegeTemplateStateModel>) {
    return this.service.loadAllPrivilegeTemplates().pipe(
      tap((allPrivilegeTemplates: IdName[]) => {
        ctx.setState(patch({ allPrivilegeTemplates }));
      })
    );
  }

  @Action(LoadPrivilegeTemplateById, { cancelUncompleted: true })
  loadPrivilegeTemplateById(
    { getState, setState }: StateContext<PrivilegeTemplateStateModel>,
    { id }: LoadPrivilegeTemplateById
  ) {
    const { privilegeTemplates } = getState();
    const exist = privilegeTemplates.some((i) => i.id === id);
    return this.service.loadPrivilegeTemplateById(id).pipe(
      tap((privilegeTemplate: IPrivilegeTemplateModel) => {
        setState(
          patch({
            privilegeTemplates: exist
              ? updateItem<IPrivilegeTemplateModel>(
                  (toUpdate) => toUpdate?.id === id,
                  privilegeTemplate
                )
              : insertItem<IPrivilegeTemplateModel>(privilegeTemplate),
            currentPrivilegeTemplate: privilegeTemplate,
          })
        );
      })
    );
  }

  @Action(CreatePrivilegeTemplate)
  @OnSuccess({
    url: AdminCompetenceRoutes.PrivilegeTemplates,
    message: 'privilegeTemplateAdded',
  })
  createPrivilegeTemplate(
    ctx: StateContext<PrivilegeTemplateStateModel>,
    { privilegeTemplate }: CreatePrivilegeTemplate
  ) {
    return this.service.createPrivilegeTemplate(privilegeTemplate);
  }

  @Action(UpdatePrivilegeTemplate)
  @OnSuccess({
    url: AdminCompetenceRoutes.PrivilegeTemplates,
    message: 'privilegeTemplateUpdated',
  })
  updatePrivilegeTemplate(
    ctx: StateContext<PrivilegeTemplateStateModel>,
    { privilegeTemplate }: UpdatePrivilegeTemplate
  ) {
    return this.service.updatePrivilegeTemplate(privilegeTemplate);
  }

  @Action(DeletePrivilegeTemplate, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminCompetenceRoutes.PrivilegeTemplates,
    message: 'privilegeTemplateDeleted',
    dispatch: ResetCurrentFilter,
  })
  deletePrivilegeTemplate(
    ctx: StateContext<PrivilegeTemplateStateModel>,
    { id }: DeletePrivilegeTemplate
  ) {
    return this.service.deletePrivilegeTemplate(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            privilegeTemplates: removeItem<IPrivilegeTemplateModel>(
              (wtToDelete: IdName | undefined) => wtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentPrivilegeTemplate)
  clearCurrentPrivilegeTemplate(ctx: StateContext<PrivilegeTemplateStateModel>) {
    ctx.setState(patch({ currentPrivilegeTemplate: undefined }));
  }

  @Selector()
  static getPrivilegeTemplatesTotalPages(state: PrivilegeTemplateStateModel) {
    return state.privilegeTemplatesTotalPages;
  }

  @Selector()
  static privilegeTemplates(state: PrivilegeTemplateStateModel) {
    return state.privilegeTemplates;
  }

  @Selector()
  static allPrivilegeTemplates(state: PrivilegeTemplateStateModel): IdName[] {
    return state.allPrivilegeTemplates;
  }

  @Selector()
  static getPrivilegeTemplateById(state: PrivilegeTemplateStateModel) {
    return (id: string) => {
      return state.privilegeTemplates.find(
        (privilegeTemplate: IPrivilegeTemplateModel) => privilegeTemplate.id === id
      );
    };
  }

  @Selector()
  static showTenantRelatedPrivilegeTemplates(state: PrivilegeTemplateStateModel) {
    return state.includeTenantRelated;
  }

  @Selector()
  static showGloballyAvailableForProductOwnersPrivilegeTemplates(
    state: PrivilegeTemplateStateModel
  ) {
    return state.includeGloballyAvailableForProductOwners;
  }

  @Selector()
  static getCurrentPrivilegeTemplate(state: PrivilegeTemplateStateModel) {
    return state.currentPrivilegeTemplate;
  }
}
